/**
 * Router — NexaCraft AI (docs/00-PRODUCT-SPEC.md § 7).
 *
 * Espaces séparés par rôle (production) :
 *  - public        : /rechercher, /professionnels/:slug, /connexion, /inscription
 *  - /pro/*        : professionnel (professional_owner / professional_agent)
 *  - /app/*        : client (Sprint 2 — tableau de bord client)
 *  - /admin/*      : administration (admin / moderator)
 *
 * Garde : sans session vérifiée → /connexion (avec retour après login).
 * Les routes pointent vers des conteneurs connectés (src/pages/connected.tsx).
 */
import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';
import { Box, CircularProgress } from '@mui/material';
import { useSelector } from 'react-redux';
import { ROUTES } from '../config/routes';
import type { RootState } from './store';
import { canAccessSpace, homeForRole } from '../lib/auth/permissions';
import { useSessionQuery } from '../lib/api/authApi';

// ── Conteneurs lazy (exports nommés de pages/connected.tsx) ────────────────
const LandingPageConnected = lazy(() =>
  import('../pages/connected').then((m) => ({ default: m.LandingPageConnected })),
);
const MarketplacePageConnected = lazy(() =>
  import('../pages/connected').then((m) => ({ default: m.MarketplacePageConnected })),
);
const DashboardViewConnected = lazy(() =>
  import('../pages/connected').then((m) => ({ default: m.DashboardViewConnected })),
);
const CallsJournalPageConnected = lazy(() =>
  import('../pages/connected').then((m) => ({ default: m.CallsJournalPageConnected })),
);
const PipelinePageConnected = lazy(() =>
  import('../pages/connected').then((m) => ({ default: m.PipelinePageConnected })),
);
const QuotaPageConnected = lazy(() =>
  import('../pages/connected').then((m) => ({ default: m.QuotaPageConnected })),
);
const ServicesPageConnected = lazy(() =>
  import('../pages/connected').then((m) => ({ default: m.ServicesPageConnected })),
);
const VoiceAgentSimulatorPageConnected = lazy(() =>
  import('../pages/connected').then((m) => ({ default: m.VoiceAgentSimulatorPageConnected })),
);
const RealtimeVoicePage = lazy(() =>
  import('../pages/professional/RealtimeVoicePage').then((m) => ({ default: m.RealtimeVoicePage })),
);
const OnboardingPageConnected = lazy(() =>
  import('../pages/connected').then((m) => ({ default: m.OnboardingPageConnected })),
);
const SettingsPageConnected = lazy(() =>
  import('../pages/connected').then((m) => ({ default: m.SettingsPageConnected })),
);
const WidgetCallPage = lazy(() =>
  import('../pages/widget/WidgetCallPage').then((m) => ({ default: m.WidgetCallPage })),
);
const DocsPage = lazy(() =>
  import('../pages/docs/DocsPage').then((m) => ({ default: m.DocsPage })),
);
const ProfileViewConnected = lazy(() =>
  import('../pages/connected').then((m) => ({ default: m.ProfileViewConnected })),
);
const AdminPageConnected = lazy(() =>
  import('../pages/connected').then((m) => ({ default: m.AdminPageConnected })),
);
const VisualSearchPage = lazy(() =>
  import('../features/visual-search').then((m) => ({ default: m.VisualSearchPage })),
);
const ClientHomePageConnected = lazy(() =>
  import('../pages/connected').then((m) => ({ default: m.ClientHomePageConnected })),
);
const LoginPage = lazy(() => import('../pages/auth/LoginPage').then((m) => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('../pages/auth/RegisterPage').then((m) => ({ default: m.RegisterPage })));
const ForgotPasswordPage = lazy(() => import('../pages/auth/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('../pages/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })));
const ClientRequestsPageConnected = lazy(() =>
  import('../pages/client/ClientPages').then((m) => ({ default: m.ClientRequestsPageConnected })),
);
const ClientCallsJournalPageConnected = lazy(() =>
  import('../pages/client/ClientExtraPages').then((m) => ({ default: m.ClientCallsJournalPageConnected })),
);
const VerifyEmailPage = lazy(() =>
  import('../pages/auth/VerifyEmailPage').then((m) => ({ default: m.VerifyEmailPage })),
);
const ClientFavoritesPageConnected = lazy(() =>
  import('../pages/client/ClientPages').then((m) => ({ default: m.ClientFavoritesPageConnected })),
);
const ClientNotificationsPageConnected = lazy(() =>
  import('../pages/client/ClientPages').then((m) => ({ default: m.ClientNotificationsPageConnected })),
);
const AdminReportsPageConnected = lazy(() =>
  import('../pages/admin/AdminExtraPages').then((m) => ({ default: m.AdminReportsPageConnected })),
);
const AdminUsersPageConnected = lazy(() =>
  import('../pages/admin/AdminExtraPages').then((m) => ({ default: m.AdminUsersPageConnected })),
);

// ── Pages publiques (Sprint A — CDC § 7) ───────────────────────────────────
const HowItWorksPage = lazy(() =>
  import('../pages/landing/PublicPages').then((m) => ({ default: m.HowItWorksPage })),
);
const PricingPage = lazy(() =>
  import('../pages/landing/PublicPages').then((m) => ({ default: m.PricingPage })),
);
const PrivacyPage = lazy(() =>
  import('../pages/landing/PublicPages').then((m) => ({ default: m.PrivacyPage })),
);
const TermsPage = lazy(() =>
  import('../pages/landing/PublicPages').then((m) => ({ default: m.TermsPage })),
);
const CategoryPage = lazy(() =>
  import('../pages/landing/PublicPages').then((m) => ({ default: m.CategoryPage })),
);

// ── Pages professionnelles supplémentaires (Sprint A) ──────────────────────
const StatisticsPageConnected = lazy(() =>
  import('../pages/professional/ProExtraPages').then((m) => ({ default: m.StatisticsPageConnected })),
);
const AbonnementPageConnected = lazy(() =>
  import('../pages/professional/ProExtraPages').then((m) => ({ default: m.AbonnementPageConnected })),
);
const PortfolioPageConnected = lazy(() =>
  import('../pages/professional/ProExtraPages').then((m) => ({ default: m.PortfolioPageConnected })),
);
const CalendarPageConnected = lazy(() =>
  import('../pages/professional/ProExtraPages').then((m) => ({ default: m.CalendarPageConnected })),
);
const ProfileEditPageConnected = lazy(() =>
  import('../pages/professional/ProfileEditPage').then((m) => ({ default: m.ProfileEditPage })),
);


// ── Pages client supplémentaires (Sprint A) ────────────────────────────────
const ClientRequestDetailPageConnected = lazy(() =>
  import('../pages/client/ClientExtraPages').then((m) => ({ default: m.ClientRequestDetailPageConnected })),
);
const ClientProfilePageConnected = lazy(() =>
  import('../pages/client/ClientExtraPages').then((m) => ({ default: m.ClientProfilePageConnected })),
);
const ClientSettingsPageConnected = lazy(() =>
  import('../pages/client/ClientExtraPages').then((m) => ({ default: m.ClientSettingsPageConnected })),
);
const ClientRecommendationsPageConnected = lazy(() =>
  import('../pages/client/ClientExtraPages').then((m) => ({ default: m.ClientRecommendationsPageConnected })),
);
const ClientSearchesPageConnected = lazy(() =>
  import('../pages/client/ClientExtraPages').then((m) => ({ default: m.ClientSearchesPageConnected })),
);

const ROUTE_FALLBACK = (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 320 }} role="status">
    <CircularProgress aria-label="Chargement du contenu" />
  </Box>
);

/**
 * Garde d'authentification + d'espace.
 *
 * Sans session vérifiée : visiteur → /connexion (avec mémorisation de
 * la destination). Un token présent mais session invalide → /connexion aussi.
 */
function RequireSpace({ space, children }: { space: 'pro' | 'client' | 'admin'; children: React.ReactNode }) {
  const location = useLocation();
  const sessionUser = useSelector((s: RootState) => s.session.user);
  const sessionChecked = useSelector((s: RootState) => s.session.sessionChecked);
  const hasToken = useSelector((s: RootState) => Boolean(s.session.token));

  if (!sessionUser) {
    // Vérification en cours (token présent, session non encore résolue) : spinner.
    if (!sessionChecked && hasToken) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 320 }} role="status">
          <CircularProgress aria-label="Vérification de la session" />
        </Box>
      );
    }
    // Pas de session : redirection vers /connexion avec destination.
    return <Navigate to={ROUTES.login} state={{ from: location.pathname }} replace />;
  }
  if (!canAccessSpace(sessionUser.role, space)) {
    // Rôle ne correspond pas à l'espace : on renvoie vers la home de son rôle.
    return <Navigate to={homeForRole(sessionUser.role)} replace />;
  }
  return <>{children}</>;
}

/**
 * Redirection par rôle : client → /app, pro → /pro (dashboard ou onboarding),
 * admin → /admin. À utiliser comme route d'index ou de fallback auth.
 */
function RoleRedirect() {
  const sessionUser = useSelector((s: RootState) => s.session.user);
  const sessionChecked = useSelector((s: RootState) => s.session.sessionChecked);
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionChecked) return;
    if (!sessionUser) {
      navigate(ROUTES.login, { replace: true });
      return;
    }
    // Pro : respecter l'état d'onboarding (voir permissions.homeForRole).
    const dest = homeForRole(sessionUser.role);
    navigate(dest, { replace: true });
  }, [sessionUser, sessionChecked, navigate]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 320 }} role="status">
      <CircularProgress aria-label="Chargement de la session" />
    </Box>
  );
}

/** Élément de page avec frontière Suspense locale. */
function Page({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={ROUTE_FALLBACK}>{children}</Suspense>;
}

const proGuard = (node: React.ReactNode) => (
  <RequireSpace space="pro">
    <Page>{node}</Page>
  </RequireSpace>
);

/** La création du compte dirige vers l'onboarding ; /pro revient toujours au dashboard. */
function ProIndexRedirect() {
  return <Navigate to={ROUTES.pro.dashboard} replace />;
}

/**
 * Initialise la session au chargement de l'app : fait un appel à /auth/session
 * pour vérifier si l'utilisateur est authentifié. Une fois la session vérifiée,
 * les gardes peuvent rediriger correctement.
 */
function SessionInitializer({ children }: { children: React.ReactNode }) {
  const [sessionStarted, setSessionStarted] = useState(false);
  const sessionResult = useSessionQuery();

  useEffect(() => {
    if (sessionResult.isLoading) return;
    setSessionStarted(true);
  }, [sessionResult.isLoading]);

  if (!sessionStarted) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 320 }} role="status">
        <CircularProgress aria-label="Vérification de la session" />
      </Box>
    );
  }

  return <>{children}</>;
}

export function AppRoutes() {
  return (
    <SessionInitializer>
    <Routes>
      {/* ── Auth (pleine page, sans chrome) ─────────────────────────────── */}
      <Route path={ROUTES.login} element={<Page><LoginPage /></Page>} />
      <Route path={ROUTES.register} element={<Page><RegisterPage /></Page>} />
      <Route path={ROUTES.verifyEmail} element={<Page><VerifyEmailPage /></Page>} />
      <Route path="/mot-de-passe-oublie" element={<Page><ForgotPasswordPage /></Page>} />
      <Route path="/reinitialisation" element={<Page><ResetPasswordPage /></Page>} />

      {/* ── Public ──────────────────────────────────────────────────────── */}
      <Route path={ROUTES.home} element={<Page><LandingPageConnected /></Page>} />
      <Route path={ROUTES.search} element={<Page><MarketplacePageConnected /></Page>} />
      <Route path="/professionnels/:slug" element={<Page><ProfileViewConnected /></Page>} />
      <Route path="/categories/:slug" element={<Page><CategoryPage /></Page>} />
      {/* Appel widget (site tiers) — publique, jeton dans le fragment d'URL. */}
      <Route path={ROUTES.widgetCall(':slug')} element={<Page><WidgetCallPage /></Page>} />
      {/* Documentation publique — sources docs/*.md. */}
      <Route path={ROUTES.docs} element={<Page><DocsPage /></Page>} />
      <Route path={ROUTES.docsItem(':docId')} element={<Page><DocsPage /></Page>} />
      <Route path={ROUTES.howItWorks} element={<Page><HowItWorksPage /></Page>} />
      <Route path={ROUTES.pricing} element={<Page><PricingPage /></Page>} />
      <Route path={ROUTES.privacy} element={<Page><PrivacyPage /></Page>} />
      <Route path={ROUTES.terms} element={<Page><TermsPage /></Page>} />

      {/* ── Espace professionnel /pro ───────────────────────────────────── */}
      <Route path={ROUTES.pro.root}>
        <Route index element={<ProIndexRedirect />} />
        <Route path="onboarding" element={proGuard(<OnboardingPageConnected />)} />
        <Route path="dashboard" element={proGuard(<DashboardViewConnected />)} />
        <Route path="appels" element={proGuard(<CallsJournalPageConnected />)} />
        <Route path="appels/:callId" element={proGuard(<CallsJournalPageConnected />)} />
        <Route path="opportunites" element={proGuard(<PipelinePageConnected />)} />
        <Route path="opportunites/:leadId" element={proGuard(<PipelinePageConnected />)} />
        <Route path="agent-vocal" element={proGuard(<VoiceAgentSimulatorPageConnected />)} />
        <Route path="agent-vocal/temps-reel" element={proGuard(<RealtimeVoicePage />)} />
        <Route path="services" element={proGuard(<ServicesPageConnected />)} />
        <Route path="realisations" element={proGuard(<PortfolioPageConnected />)} />
        <Route path="calendrier" element={proGuard(<CalendarPageConnected />)} />
        <Route path="statistiques" element={proGuard(<StatisticsPageConnected />)} />
        <Route path="abonnement" element={proGuard(<AbonnementPageConnected />)} />
        <Route path="consommation" element={proGuard(<QuotaPageConnected />)} />
        <Route path="parametres" element={proGuard(<SettingsPageConnected />)} />
        <Route
          path="profil"
          element={
            <Page>
              <ProfileViewConnected />
            </Page>
          }
        />
        <Route path="profil-edit" element={proGuard(<ProfileEditPageConnected />)} />
      </Route>

      {/* ── Espace client /app ──────────────────────────────────────────── */}
      <Route path={ROUTES.app.root}>
        <Route
          index
          element={
            <RequireSpace space="client">
              <Page><ClientHomePageConnected /></Page>
            </RequireSpace>
          }
        />
        <Route path="demandes" element={<RequireSpace space="client"><Page><ClientRequestsPageConnected /></Page></RequireSpace>} />
        <Route path="appels" element={<RequireSpace space="client"><Page><ClientCallsJournalPageConnected /></Page></RequireSpace>} />
        <Route path="demandes/:requestId" element={<RequireSpace space="client"><Page><ClientRequestDetailPageConnected /></Page></RequireSpace>} />
        <Route path="favoris" element={<RequireSpace space="client"><Page><ClientFavoritesPageConnected /></Page></RequireSpace>} />
        <Route path="recommandations" element={<RequireSpace space="client"><Page><ClientRecommendationsPageConnected /></Page></RequireSpace>} />
        <Route path="recherches" element={<RequireSpace space="client"><Page><ClientSearchesPageConnected /></Page></RequireSpace>} />
        <Route path="scanner" element={<RequireSpace space="client"><Page><VisualSearchPage /></Page></RequireSpace>} />
        <Route path="notifications" element={<RequireSpace space="client"><Page><ClientNotificationsPageConnected /></Page></RequireSpace>} />
        <Route path="profil" element={<RequireSpace space="client"><Page><ClientProfilePageConnected /></Page></RequireSpace>} />
        <Route path="parametres" element={<RequireSpace space="client"><Page><ClientSettingsPageConnected /></Page></RequireSpace>} />
        {/* Redirections d'anciennes URLs éventuelles → tableau de bord. */}
        <Route path="*" element={<Navigate to={ROUTES.app.root} replace />} />
      </Route>

      {/* ── Administration /admin ───────────────────────────────────────── */}
      {/* Routes indépendantes : AdminPage ne rend pas d'Outlet, chaque URL
          porte donc sa propre console ouverte sur l'onglet demandé. */}
      <Route path={ROUTES.admin.root} element={<RequireSpace space="admin"><Page><AdminPageConnected /></Page></RequireSpace>} />
      <Route path={ROUTES.admin.verifications} element={<RequireSpace space="admin"><Page><AdminPageConnected initialTab="verifications" /></Page></RequireSpace>} />
      <Route path={ROUTES.admin.audit} element={<RequireSpace space="admin"><Page><AdminPageConnected initialTab="audit" /></Page></RequireSpace>} />
      <Route path={`${ROUTES.admin.root}/stats`} element={<RequireSpace space="admin"><Page><AdminPageConnected initialTab="stats" /></Page></RequireSpace>} />
      <Route path={ROUTES.admin.reports} element={<RequireSpace space="admin"><Page><AdminPageConnected initialTab="reports" /></Page></RequireSpace>} />
      <Route path={ROUTES.admin.users} element={<RequireSpace space="admin"><Page><AdminPageConnected initialTab="users" /></Page></RequireSpace>} />
      <Route path={ROUTES.admin.dashboard} element={<Navigate to={ROUTES.admin.root} replace />} />

      {/* ── Fallbacks ───────────────────────────────────────────────────── */}
      <Route path="*" element={<Navigate to={ROUTES.home} replace />} />
    </Routes>
    </SessionInitializer>
  );
}
